import { Check } from "lucide-react";
import { Timestamp } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";
import type { EscrowLifecycle, EscrowLifecycleStep } from "@/types/escrow";

/**
 * Horizontal lifecycle indicator for an escrow transaction. Reached steps show
 * a gold check + relative timestamp (exact on hover); the current step (first
 * not-yet-reached) is bold with a subtle pulse; future steps are muted. All
 * booleans/timestamps are derived server-side (the `lifecycle` DTO) so this
 * component never parses the raw Escrow.com payload.
 */

const STEPS: { key: keyof EscrowLifecycle; label: string }[] = [
  { key: "drafted", label: "Drafted" },
  { key: "approved", label: "Approved by Phoenix" },
  { key: "sentToEscrow", label: "Sent to Escrow.com" },
  { key: "buyerAgreed", label: "Buyer agreed" },
  { key: "buyerFunded", label: "Buyer funded" },
  { key: "disbursed", label: "Disbursed to Phoenix" },
  { key: "closed", label: "Closed" },
];

export function EscrowLifecycleIndicator({ lifecycle }: { lifecycle: EscrowLifecycle }) {
  const fmt = useDateFormatter();
  const currentIndex = STEPS.findIndex((s) => !(lifecycle[s.key] as EscrowLifecycleStep).reached);

  return (
    <ol
      className="flex items-start gap-space-2 overflow-x-auto list-none p-0 m-0"
      aria-label="Escrow lifecycle"
    >
      {STEPS.map((step, idx) => {
        const value = lifecycle[step.key] as EscrowLifecycleStep;
        const isReached = value.reached;
        const isCurrent = idx === currentIndex;
        return (
          <li
            key={step.key}
            className="flex flex-col items-center text-center gap-space-1 min-w-[88px] shrink-0"
            data-step={step.key}
            data-state={isReached ? "reached" : isCurrent ? "current" : "upcoming"}
          >
            <div className="flex items-center w-full">
              <span
                className={cn(
                  "h-px flex-1",
                  idx === 0 ? "opacity-0" : isReached ? "bg-status-success" : "bg-border-light",
                )}
                aria-hidden="true"
              />
              <span
                className={cn(
                  "inline-flex items-center justify-center w-7 h-7 rounded-full border text-caption font-semibold shrink-0",
                  isReached && "border-status-success bg-status-success/15 text-status-success",
                  isCurrent && "border-gold bg-gold text-onyx animate-pulse",
                  !isReached && !isCurrent && "border-border-light bg-white-warm text-gray-mid",
                )}
                aria-current={isCurrent ? "step" : undefined}
              >
                {isReached ? <Check size={14} aria-hidden /> : idx + 1}
              </span>
              <span
                className={cn(
                  "h-px flex-1",
                  idx === STEPS.length - 1
                    ? "opacity-0"
                    : (lifecycle[STEPS[idx + 1].key] as EscrowLifecycleStep).reached
                      ? "bg-status-success"
                      : "bg-border-light",
                )}
                aria-hidden="true"
              />
            </div>
            <span
              className={cn(
                "text-caption leading-tight px-space-1",
                isCurrent ? "text-onyx font-semibold" : isReached ? "text-onyx" : "text-gray-mid",
              )}
            >
              {step.label}
            </span>
            {isReached && value.at ? (
              <Timestamp value={value.at} tz={fmt.tz} className="text-caption" />
            ) : (
              <span className="text-caption text-gray-mid" aria-hidden="true">
                &nbsp;
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
